import { useRef, useState, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

function PlayIcon() {
  return (
    <motion.svg
      key="play"
      viewBox="0 0 24 24"
      className="w-[18px] h-[18px] -translate-x-[0.5px]"
      fill="currentColor"
      initial={{ scale: 0.5, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.5, opacity: 0 }}
      transition={{ duration: 0.15, ease: [0.4, 0, 0.1, 1] }}
    >
      <path d="M8 5v14l11-7z" />
    </motion.svg>
  )
}

function PauseIcon() {
  return (
    <motion.svg
      key="pause"
      viewBox="0 0 24 24"
      className="w-[18px] h-[18px] -translate-x-[0.5px]"
      fill="currentColor"
      initial={{ scale: 0.5, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.5, opacity: 0 }}
      transition={{ duration: 0.15, ease: [0.4, 0, 0.1, 1] }}
    >
      <rect x="6" y="5" width="4" height="14" rx="1" />
      <rect x="14" y="5" width="4" height="14" rx="1" />
    </motion.svg>
  )
}

export default function TobiCharacterIllustration() {
  const videoRef = useRef<HTMLVideoElement>(null)
  const rafRef = useRef<number>(0)
  const [progress, setProgress] = useState(0)
  const [playing, setPlaying] = useState(false)

  const updateProgress = useCallback(() => {
    const video = videoRef.current
    if (video && video.duration) {
      setProgress(video.currentTime / video.duration)
    }
    if (playing) {
      rafRef.current = requestAnimationFrame(updateProgress)
    }
  }, [playing])

  useEffect(() => {
    if (playing) {
      rafRef.current = requestAnimationFrame(updateProgress)
    }
    return () => cancelAnimationFrame(rafRef.current)
  }, [playing, updateProgress])

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    function onEnded() {
      setPlaying(false)
      setProgress(1)
    }

    video.addEventListener('ended', onEnded)
    return () => video.removeEventListener('ended', onEnded)
  }, [])

  function handleScrub(e: React.ChangeEvent<HTMLInputElement>) {
    const value = parseFloat(e.target.value)
    setProgress(value)
    if (videoRef.current) {
      videoRef.current.currentTime = value * videoRef.current.duration
    }
  }

  function togglePlay() {
    const video = videoRef.current
    if (!video) return
    if (playing) {
      video.pause()
    } else {
      if (video.ended) {
        video.currentTime = 0
        setProgress(0)
      }
      video.play()
    }
    setPlaying(!playing)
  }

  return (
    <div className="rounded-xl overflow-hidden bg-surface-hover">
      <div className="aspect-square">
        <video
          ref={videoRef}
          className="w-full h-full object-cover"
          src="/videos/tobi/tobi-scrub.mp4"
          muted
          playsInline
          preload="auto"
        />
      </div>
      <div className="px-4 py-3 flex items-center gap-3">
        <motion.button
          onClick={togglePlay}
          className="shrink-0 w-8 h-8 rounded-full bg-[#E60000] flex items-center justify-center text-white hover:bg-[#CC0000] transition-colors"
          aria-label={playing ? 'Pause' : 'Play'}
          whileTap={{ scale: 0.9 }}
        >
          <AnimatePresence mode="wait">
            {playing ? <PauseIcon /> : <PlayIcon />}
          </AnimatePresence>
        </motion.button>
        <input
          type="range"
          min={0}
          max={1}
          step={0.005}
          value={progress}
          onChange={handleScrub}
          className="flex-1 h-1.5 rounded-full appearance-none cursor-pointer bg-text-muted/60 accent-[#E60000]"
        />
      </div>
    </div>
  )
}
